import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { Drawer, Tag } from "antd5";
import { isEqual } from "lodash";

import { Input } from "components/form_components/Inputs";
import { FrameworkCallOffTableCell } from "components/framework/FrameworkCallOffTableCell";
import { getSortOrder } from "components/reports/builder_components/SupplierTable";
import Button from "components/ui/button/Button";
import { appRoutes } from "lib/appRoutes";
import { ClearSelectedButton } from "lib/core_components/ClearSelectedButton";
import { ColumnHeader } from "lib/core_components/ColumnHeader";
import { useTableRowClickthrough } from "lib/core_components/commonTableItems";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { ColumnType } from "lib/core_components/Table/ColumnTypes";
import { Table } from "lib/core_components/Table/Table";
import { createUseDebounce } from "lib/debounce";
import { useBuyerLists } from "lib/hooks/api/buyer_lists/useBuyerLists";
import {
  FrameworkOrganisation,
  SearchFrameworkOrganisationsResult,
  useSearchFrameworkBuyers,
} from "lib/hooks/api/frameworks/useSearchFrameworkOrganisations";
import { useTablePageSize } from "lib/hooks/useTablePageSize";
import {
  parsePaginationUrlState,
  useResetPageWhenPastEnd,
  useURLState,
} from "lib/hooks/useURLState";
import { PaginationState, SortState } from "lib/search/types";
import { EventData, EventDataTypes } from "lib/tracking";
import { ColumnSetting } from "lib/types/models";
import { toHumanRelativeDateFromNow } from "lib/utils/relative_time";
import { useFormAutoSubmit } from "../../lib/hooks/useFormAutoSubmit";
import { SaveListDrawerProvider } from "../entities/save_components/lists/SaveListDrawerProvider";
import { BuyerFilterForm, BuyerFilterSections } from "./BuyerFilterForm";
import { ExportFrameworkBuyersButton } from "./ExportFrameworkBuyersButton";
import { NameColumn } from "./NameColumn";
import { QuickFilterBarFrameworkBuyers } from "./QuickFilterBarFrameworkBuyers";
import { SaveBuyerButton } from "./SaveBuyerButton";
import { TableData } from "./types";
import {
  DEFAULT_LEGACY_BUYER_FILTERS,
  LegacyBuyerTableFilters,
  useLegacyFilterableBuyerTableState,
} from "./useLegacyFilterableBuyerTableState";

import css from "./FrameworkBuyersTable.module.scss";

const ROLE_LABEL_BY_ROLE: Record<string, string> = {
  ContractingAuthority: "Contracting authority",
  TransactingBuyer: "Transacting buyer",
  Provider: "Provider",
};

function getBuyerColumns({
  sortOrder,
  selectedColumns,
  trackingData,
  frameworkId,
  savedListsByBuyerId,
}: {
  sortOrder: SortState;
  selectedColumns: FrameworkBuyerColumn[];
  trackingData?: EventData;
  frameworkId: string;
  savedListsByBuyerId: Map<string, { id: string; name: string }[]>;
}) {
  const name: ColumnType<FrameworkOrganisation> = {
    key: "buyer_name",
    title: <ColumnHeader header=" Buyer name" subHeader="&amp; buyer type" />,
    render: (_, buyer) => {
      const savedLists = savedListsByBuyerId.get(buyer.id) ?? [];
      return (
        <NameColumn
          buyerGuid={buyer.id}
          buyerName={buyer.name}
          isSupplierRelationship={false}
          trackingData={trackingData}
          savedLists={savedLists}
        />
      );
    },
    sorter: true,
    sortOrder: getSortOrder(sortOrder, "buyer_name"),
    sizeConfig: {
      small: 440,
      medium: 440,
      large: 480,
      xlarge: 520,
    },
  };
  const role: ColumnType<FrameworkOrganisation> = {
    key: "role",
    title: "Role",
    render: (_, b) => {
      return b.roles.map((r) => <Tag key={r}>{ROLE_LABEL_BY_ROLE[r] || r}</Tag>);
    },
  };

  const frameworkLastActivity: ColumnType<FrameworkOrganisation> = {
    key: "frameworkLastActivity",
    title: "Last activity",
    render: (_, b) => {
      const lastActivity = b.callOffs?.lastActivity;
      return lastActivity ? toHumanRelativeDateFromNow(lastActivity) : "--";
    },
    sorter: true,
    sortOrder: getSortOrder(sortOrder, "frameworkLastActivity"),
    sortDirections: ["descend", "ascend", "descend"],
  };

  const frameworkCallOffs: ColumnType<FrameworkOrganisation> = {
    key: "frameworkCallOffs",
    title: "Call offs",
    render: (_, b) => {
      return (
        <FrameworkCallOffTableCell
          averageAmount={b.callOffs?.averageAmount.amount}
          callOffCount={b.callOffs?.count}
          currency={b.callOffs?.averageAmount.currency}
          linkTo={`/frameworks/${frameworkId}/call-offs?noticeFilters[buyers][]=${b.id}&noticeFilters[frameworkIds][]=${frameworkId}`}
        />
      );
    },
    sorter: true,
    sortOrder: getSortOrder(sortOrder, "frameworkCallOffs"),
    sortDirections: ["descend", "ascend", "descend"],
  };

  const columns = { name, role, frameworkLastActivity, frameworkCallOffs };
  return selectedColumns.map((column) => columns[column]);
}

/**
 * This table is a bit different from the regular buyers table - the data it displays is quite
 * framework-specific (showing how buyers intersect with a specific framework) - and probably
 * doesn't make sense in more general buyer contexts
 */
function FrameworkBuyersTable({
  sortOrder,
  onSortChange,
  selectedRows,
  onSelectedRowsChange,
  selectedColumns,
  frameworkId,
  tableData,
  pagination,
  onPaginationChange,
}: {
  filters: LegacyBuyerTableFilters;
  sortOrder: SortState;
  onSortChange: (sort: SortState) => void;
  selectedRows?: string[];
  onSelectedRowsChange: (selectedRowKeys: string[], selectedRows: FrameworkOrganisation[]) => void;
  selectedColumns: FrameworkBuyerColumn[];
  frameworkId: string;
  pagination: PaginationState;
  onPaginationChange: (pagination: PaginationState) => void;
  // TODO: Fetch this from react context using a hook
  tableData: TableData<SearchFrameworkOrganisationsResult>;
}) {
  const { data: listsData } = useBuyerLists();

  const savedListsByBuyerId = useMemo((): Map<string, { id: string; name: string }[]> => {
    const map = new Map<string, { id: string; name: string }[]>();
    for (const list of listsData?.all ?? []) {
      for (const entry of list.entries) {
        const current = map.get(entry.id) ?? [];
        map.set(entry.id, [...current, { id: list.id, name: list.name }]);
      }
    }
    return map;
  }, [listsData?.all]);

  const columns = getBuyerColumns({
    sortOrder,
    selectedColumns,
    frameworkId,
    savedListsByBuyerId,
  });
  const { queryResult } = tableData;

  const onRow = useTableRowClickthrough<FrameworkOrganisation>((r) => appRoutes.buyerDetail(r.id));

  return (
    <SaveListDrawerProvider>
      <Table<FrameworkOrganisation>
        dataSource={queryResult.data?.organisations || []}
        columns={columns}
        loading={queryResult.isLoading}
        isError={queryResult.isError}
        pagination={{
          ...pagination,
          total: tableData.totalCount ? tableData.totalCount + 1 : 0,
          onChange: (page, pageSize) => onPaginationChange({ current: page, pageSize }),
          showSizeChanger: true,
        }}
        onChange={(_, __, sorter) => {
          if (Array.isArray(sorter)) {
            sorter = sorter[0];
          }
          // If the sort order is not defined, we don't want to trigger a sort without an order
          if (!sorter.order) {
            return;
          }
          const newSortState: SortState = {
            order: sorter.order === "descend" ? "DESC" : "ASC",
            field: sorter.columnKey as string,
          };
          if (!isEqual(newSortState, sortOrder)) {
            onSortChange(newSortState);
          }
        }}
        rowSelection={
          selectedRows && {
            onChange: (selectedRowKeys, selectedRows) => {
              onSelectedRowsChange(selectedRowKeys as string[], selectedRows);
            },
            preserveSelectedRowKeys: true,
            selectedRowKeys: selectedRows,
          }
        }
        rowKey="id"
        onRow={onRow}
      />
    </SaveListDrawerProvider>
  );
}

type FrameworkBuyerColumn = "name" | "frameworkCallOffs" | "role" | "frameworkLastActivity";

const COLUMNS: ColumnSetting<FrameworkBuyerColumn>[] = [
  {
    title: "Buyer name",
    field: "name",
  },
  {
    title: "Call offs",
    field: "frameworkCallOffs",
  },
  {
    title: "Role",
    field: "role",
  },
  {
    title: "Last activity",
    field: "frameworkLastActivity",
  },
];

const DEFAULT_SORT: SortState = {
  field: "buyer_name",
  order: "ASC",
};

export function FilterableFrameworkBuyersTable({
  hiddenSections,
  frameworkId,
}: {
  defaultSortOrder?: SortState;
  hiddenSections?: BuyerFilterSections[];
  defaultColumns?: ColumnSetting<FrameworkBuyerColumn>[];
  allColumns?: ColumnSetting<FrameworkBuyerColumn>[];
  frameworkId: string;
}) {
  const tableState = useLegacyFilterableBuyerTableState(DEFAULT_LEGACY_BUYER_FILTERS, DEFAULT_SORT);
  const { buyerTableFilters, setBuyerTableFilters, buyerSort, setBuyerSort } = tableState;
  // Page and page size live in the URL, so Back and shared links show the same rows. Keyed per
  // table because profile tabs carry the URL's search params from one tab to the next.
  const [storedPageSize, setStoredPageSize] = useTablePageSize("framework-buyers-table");
  const [pagination = { page: 1, pageSize: storedPageSize }, setPagination] = useURLState(
    "frameworkBuyersPagination",
    { page: 1, pageSize: storedPageSize },
    (state) => parsePaginationUrlState(state, { page: 1, pageSize: storedPageSize }),
  );
  const currentPage = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? storedPageSize;
  const setCurrentPage = useCallback(
    (page: number) => setPagination((prev) => ({ ...prev, page })),
    [setPagination],
  );
  const setPageSize = useCallback(
    (size: number) => {
      setStoredPageSize(size);
      setPagination((prev) => ({ ...prev, pageSize: size }));
    },
    [setStoredPageSize, setPagination],
  );

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const [selectedColumns, setSelectedColumns] = useState(COLUMNS);
  const [selectedBuyers, setSelectedBuyers] = useState<string[]>([]);

  const updateFilters = useCallback(
    (filters: LegacyBuyerTableFilters) => {
      setBuyerTableFilters(filters);
      // Always reset pagination when filters change
      setCurrentPage(1);
    },
    [setBuyerTableFilters, setCurrentPage],
  );

  const useDebounce500 = createUseDebounce(500);
  const debouncedSetBuyerTableFilters = useDebounce500((filters) => {
    updateFilters(filters);
  });

  const values = buyerTableFilters;

  const { control, getValues, trigger, watch } = useForm({
    defaultValues: {
      buyerName: buyerTableFilters.buyerName,
    },
    mode: "onChange",
    values,
  });

  useFormAutoSubmit({
    watch,
    getValues,
    trigger,
    onChange: (data) => {
      debouncedSetBuyerTableFilters({ ...buyerTableFilters, buyerName: data.buyerName });
    },
  });

  const updateSortState = useCallback(
    (newSortState: SortState) => {
      setBuyerSort(newSortState);
      // Always reset pagination when sort changes
      setCurrentPage(1);
    },
    [setBuyerSort, setCurrentPage],
  );

  const queryResult = useSearchFrameworkBuyers(frameworkId, buyerTableFilters, buyerSort, {
    current: currentPage,
    pageSize,
  });

  useResetPageWhenPastEnd(currentPage, pageSize, queryResult.data?.totalCount, setCurrentPage);

  const tableData: TableData<SearchFrameworkOrganisationsResult> = {
    queryResult,
    totalCount: queryResult.data?.totalCount,
  };

  return (
    <>
      <div className={css.filterBar}>
        <div className={css.filterItems}>
          <form
            className={css.searchInput}
            onSubmit={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              name="buyerName"
              label=""
              control={control}
              allowClear
            />
          </form>
          <Button buttonType="secondary" onClick={() => setIsFilterDrawerOpen(true)}>
            <FilterOutlined className={css.filterIcon} /> Filters
          </Button>
        </div>
        <div className={css.columnsAndExport}>
          <div className={css.clearSelected}>
            {selectedBuyers.length > 0 && (
              <ClearSelectedButton
                count={selectedBuyers.length}
                onClear={() => setSelectedBuyers([])}
              />
            )}
          </div>
          {selectedBuyers && selectedBuyers.length === 0 && (
            <EditColumnsButton
              allAvailableColumns={COLUMNS}
              selectedColumns={selectedColumns}
              onNewColumnSettingChange={(c) =>
                setSelectedColumns([...(c as ColumnSetting<FrameworkBuyerColumn>[])])
              }
              dataType={EventDataTypes.buyer}
            />
          )}
          <div className={css.saveBuyers}>
            {selectedBuyers.length > 0 && (
              <SaveBuyerButton
                buyerGuids={selectedBuyers}
                trackingData={{ "Context source": "Bulk action" }}
              />
            )}
          </div>
          <ExportFrameworkBuyersButton
            filters={buyerTableFilters}
            selectedBuyers={selectedBuyers}
            tableData={tableData}
            frameworkId={frameworkId}
          />
        </div>
      </div>
      <div className={css.viewFilters}>
        <QuickFilterBarFrameworkBuyers
          filters={buyerTableFilters}
          sortState={buyerSort}
          tableData={tableData}
          onClearFilter={(key) => updateFilters({ ...buyerTableFilters, [key]: undefined })}
          onFilterClick={() => setIsFilterDrawerOpen(true)}
          hiddenSections={hiddenSections || []}
          clearAllFilters={() => updateFilters(DEFAULT_LEGACY_BUYER_FILTERS)}
        />
      </div>
      <FrameworkBuyersTable
        tableData={tableData}
        filters={buyerTableFilters}
        sortOrder={buyerSort}
        onSortChange={updateSortState}
        selectedRows={selectedBuyers}
        onSelectedRowsChange={(rows) => setSelectedBuyers(rows)}
        selectedColumns={selectedColumns.map((column) => column.field)}
        frameworkId={frameworkId}
        pagination={{ current: currentPage, pageSize }}
        onPaginationChange={({ current, pageSize }) => {
          setPageSize(pageSize);
          setCurrentPage(current);
        }}
      />
      {isFilterDrawerOpen && (
        <Drawer
          title="Filters"
          onClose={() => setIsFilterDrawerOpen(false)}
          open={isFilterDrawerOpen}
          size="large"
        >
          <BuyerFilterForm
            filters={buyerTableFilters}
            onFiltersChange={debouncedSetBuyerTableFilters}
            options={{
              disabledSections: hiddenSections,
            }}
            onClose={() => setIsFilterDrawerOpen(false)}
          />
        </Drawer>
      )}
    </>
  );
}
